import { ApiService } from "./api.service";
import { IResponse } from "../models/response";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import {
  ComponentsModel,
  ComponentDetailsModel,
  ComponentOptionsModel,
  ComponentAuditsModel,
} from "../models/components/components.model";
import { InfoModel } from "../models/info.model";
import { useContext } from "react";
import { AbilityContext } from "../casl/Can";
import { AxiosError } from "axios";
import { userRoleKey } from "../utils/helper";
import { Subjects } from "../casl/ability";

/**
 * Hook to fetch a list of components for a specific plant.
 *
 * @param {string} id - Plant ID.
 * @param {number} per_page - Items per page.
 * @param {number} page - Current page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting.
 * @returns {Object} A React Query object with components list and a `refetch` function.
 */
export const useFetchComponents = (
  id: string,
  per_page: number,
  page: number,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>,
  enabled = true
) => {
  const queryKey = [
    "plants",
    id,
    "components",
    per_page,
    page,
    JSON.stringify(filters),
    sort,
  ];
  const { refetch, ...queryResult } = useQuery<
    IResponse<ComponentsModel[]>,
    Error
  >(queryKey, async () => {
    const params = {
      page: page,
      per_page: per_page,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const subTasksData = await ApiService.get(`/plants/${id}/components`, {
      params,
    });
    return subTasksData.data;
  },{
    enabled: enabled,
  });

  return { ...queryResult, refetch };
};

/**
 * Hook to fetch available component types.
 *
 * @returns {Object} A React Query object with component types.
 */
export const useFetchComponentsType = () => {
  const queryKey = ["component-types"];
  return useQuery<InfoModel[], Error>(queryKey, async () => {
    try {
      const componentsData = await ApiService.get(`/component-types`);
      return componentsData?.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Hook to fetch available component options.
 *
 * @returns {Object} A React Query object with component options.
 */
export const useFetchComponentOptions = () => {
  const queryKey = ["component-options"];
  return useQuery<ComponentOptionsModel, Error>(queryKey, async () => {
    try {
      const componentsData = await ApiService.get(`/component-options`);
      return componentsData?.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Hook to create a new component under a specific plant.
 *
 * @param {string} plantId - Plant ID.
 * @returns {Object} A React Query mutation object to create a component using FormData.
 */
export const useCreateComponent = (plantId: string) => {
  const createComponent: MutationFunction<IResponse<any>, FormData> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      `/plants/${plantId}/components`,
      formData
    );
    return response.data;
  };
  return useMutation(createComponent);
};

/**
 * Hook to fetch details of a specific component.
 *
 * @param {string} [id] - Component ID.
 * @returns {Object} A React Query object with component details and a `refetch` function.
 */
export const useFetchComponent = (id?: string) => {
  const queryKey = ["components", id];

  const { refetch, ...queryResult } = useQuery<
    ComponentDetailsModel,
    AxiosError
  >(
    queryKey,
    async () => {
      const componentData = await ApiService.get(`/components/${id}`);
      return componentData;
    },
    {
      staleTime: 60 * 1000,
      enabled: Boolean(id),
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch detailed information for a specific component.
 *
 * @param {string} [componentId] - Component ID.
 * @returns {Object} A React Query object with detailed component info.
 */
export const useFetchComponentDetails = (componentId?: string) => {
  const queryKey = ["components", componentId];
  return useQuery<ComponentDetailsModel, Error>(queryKey, async () => {
    try {
      const componentsData = await ApiService.get(`/components/${componentId}`);
      return componentsData?.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Hook to fetch audit logs for a specific component.
 *
 * @param {string} componentId - Component ID.
 * @param {number} per_page - Items per page.
 * @param {number} page - Current page.
 * @returns {Object} A React Query object with component audits.
 */
export const useFetchComponentAudits = (
  componentId: string,
  per_page: number,
  page: number
) => {
  const queryKey = ["components", componentId, "audits"];
  return useQuery<IResponse<ComponentAuditsModel[]>, Error>(
    queryKey,
    async () => {
      try {
        const componentAudits = await ApiService.get(
          `/components/${componentId}/audits?per_page=${per_page}&page=${page}`
        );
        return componentAudits?.data;
      } catch (error) {
        throw error;
      }
    }
  );
};

/**
 * Function to delete a component by its ID.
 *
 * @param {string} [componentId] - Component ID.
 * @returns {Promise<any>} Promise resolving to deletion response.
 */
export const deleteComponent = async (componentId?: string) => {
  try {
    const response = await ApiService.delete(`/components/${componentId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Hook to update an existing component.
 *
 * @param {string} [id] - Component ID.
 * @returns {Object} A React Query mutation object to update component data.
 */
export const useUpdateComponent = (id?: string) => {
  const UpdateComponent: MutationFunction<IResponse<any>, any> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      `/components/${id}?_method=PATCH`,
      formData
    );
    return response;
  };
  return useMutation(UpdateComponent);
};

/**
 * Hook to bulk update multiple components.
 *
 * @returns {Object} A React Query mutation object for bulk update.
 */
export const useUpdateComponents = () => {
  const UpdateComponent: MutationFunction<IResponse<any>, any> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      `/components?_method=PATCH`,
      formData
    );
    return response;
  };
  return useMutation(UpdateComponent);
};

/**
 * Hook to import apartment unit components from Excel file.
 *
 * @param {string} plantId - Plant ID.
 * @returns {Object} A React Query mutation object to import apartment unit components.
 */
export const useImportApartmentUnitComponents = (plantId: string) => {
  const importComponents: MutationFunction<IResponse<any>, FormData> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      `/plants/${plantId}/components/import`,
      formData
    );
    return response.data;
  };
  return useMutation(importComponents);
};

/**
 * Hook to fetch current user's tasks (today or prior).
 *
 * @param {Object} params - Parameters including user type, pagination, filters, sort.
 * @returns {Object} A React Query object with the list of current tasks.
 */
export const useFetchCurrentUserTasks = (params: {
  user_type: "other" | "self";
  page?: number;
  itemsPerPage: number;
  filters?: Record<string, unknown>;
  sort?: Record<string, unknown>;
}) => {

  const queryKey = [`user-current-tasks-${params.user_type}`, params.page,
  params.itemsPerPage,
  params.filters,
  params.sort];
  const ability = useContext(AbilityContext);
  const componentsData = () => {
    const currentUserRole = userRoleKey ?? "0";
    let permission : Subjects;
    if(currentUserRole == "client" || currentUserRole == "client_worker") {
      permission = "Client Current Activities";
    } else {
      permission = params.user_type === "other"
        ? "Other Current Activities"
        : "Self Current Activities";
    }

    if (
      ability.can(
        "view",
        permission
      )
    ) {
      const requestParams = {
        user_type: params.user_type,
        page: params.page,
        per_page: params.itemsPerPage,
        ...params.filters,
        ...params.sort,
      };
      return ApiService.get(`/today-or-prior-activities`, { params: requestParams });
    } else {
      return null;
    }
  };
  const enabledSelf =
    ability.can("view", "Self Current Activities") ||
    ability.can("view", "Client Current Activities");
  const enabledOther = ability.can("view", "Other Current Activities");
  const enabled =
    params.user_type === "other" ? enabledOther : enabledSelf;
  return useQuery({
    queryFn: componentsData,
    queryKey,
    enabled,
    select: (data) => data.data,
  });
};

/**
 * Hook to fetch upcoming tasks for the user.
 *
 * @param {Object} params - Parameters including user type, pagination, filters, sort.
 * @returns {Object} A React Query object with upcoming tasks list.
 */
export const useFetchUpComingUserTasks = (params: {
  user_type: "other" | "self";
  page?: number;
  itemsPerPage: number;
  filters?: Record<string, unknown>;
  sort?: Record<string, unknown>;
}) => {
  const queryKey = [`user-upcoming-tasks-${params.user_type}`, params.page,
  params.itemsPerPage,
  params.filters,
  params.sort];
  const ability = useContext(AbilityContext);

  const componentsData = () => {
    if (
      ability.can(
        "view",
        params.user_type === "other"
          ? "Other Upcoming Activities"
          : "Self Upcoming Activities"
      )
    ) {
      const requestParams = {
        user_type: params.user_type,
        page: params.page,
        per_page: params.itemsPerPage,
        ...params.filters,
        ...params.sort,
      };
      return ApiService.get(`/upcoming-activities`, { params: requestParams });
    } else return null;
  };
  return useQuery({
    queryFn: componentsData,
    queryKey: queryKey,
    enabled: ability.can(
      "view",
      params.user_type === "other"
        ? "Other Upcoming Activities"
        : "Self Upcoming Activities"
    ),
    select: (data) => data.data,
  });
};

/**
 * Hook to fetch disturbances for the client.
 *
 * @param {Object} params - Parameters including pagination, filters, sort.
 * @returns {Object} A React Query object with client disturbances issues.
 */
export const useFetchDisturbancesClient = (params: {
  page?: number;
  itemsPerPage: number;
  filters?: Record<string, unknown>;
  sort?: Record<string, unknown>;
}) => {
  const queryKey = [`client-disturbances-issues`, params.page,
    params.itemsPerPage,
    params.filters,
    params.sort];
  const ability = useContext(AbilityContext);
  const componentsData = () => {
    if (ability.can("view", "Disturbances Client")) {
      const requestParams = {
        page: params.page,
        per_page: params.itemsPerPage,
        ...params.filters,
        ...params.sort,
      };
      return ApiService.get(`/client-disturbances`, { params: requestParams });
    } else {
      return null;
    }
  };
  return useQuery({
    queryFn: componentsData,
    queryKey: queryKey,
    enabled: ability.can("view", "Disturbances Client"),
  });
};
